import {
  BookOpen,
  Trophy,
  Clock,
  Target,
  TrendingUp,
  Award,
  CheckCircle,
  Calendar,
  Flag,
  Zap,
} from "lucide-react";
import UniversityStudentLayout from "../../layouts/UniversityStudentLayout";
import "../../assets/styles/colors.css";

const activeCourses = [
  {
    title: "Network Security Fundamentals",
    progress: 65,
    flagsCaptured: 12,
    nextDeadline: "3 days",
    status: "In Progress",
  },
  {
    title: "Web Application Security",
    progress: 45,
    flagsCaptured: 8,
    nextDeadline: "5 days",
    status: "In Progress",
  },
  {
    title: "Ethical Hacking Basics",
    progress: 85,
    flagsCaptured: 18,
    nextDeadline: "1 day",
    status: "Near Completion",
  },
];

const recentAchievements = [
  {
    title: "Flag Hunter",
    description: "Captured 50 flags",
    icon: Flag,
    color: "bg-yellow-500/20 text-yellow-400",
    date: "2 days ago",
  },
  {
    title: "Speed Demon",
    description: "Completed a lab in under 10 minutes",
    icon: Zap,
    color: "bg-blue-500/20 text-blue-400",
    date: "1 week ago",
  },
  {
    title: "Course Master",
    description: "Completed Network Security course",
    icon: Award,
    color: "bg-green-500/20 text-green-400",
    date: "2 weeks ago",
  },
];

export default function UniversityStudentDashboard() {
  return (
    <UniversityStudentLayout>
      <div className="px-8 py-8 space-y-8">
        {/* Header */}
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-(--color-text-muted)">
            My Studies
          </div>
          <h1 className="qv-serif mt-2 text-[40px] md:text-[44px] leading-none font-medium tracking-tight text-(--color-text-primary)">
            Welcome back.
          </h1>
          <p className="mt-2 text-sm text-(--color-text-secondary)">
            Track your learning progress and achievements.
          </p>
        </div>

        {/* Progress hero */}
        <div
          className="qv-card qv-noise p-6 flex flex-col md:flex-row md:items-center justify-between gap-6"
          style={{
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 10%, transparent), color-mix(in srgb, var(--color-accent-violet) 6%, transparent) 60%, transparent)",
          }}
        >
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-(--color-text-muted)">
              Semester Progress
            </div>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="qv-num text-[44px] leading-none text-(--color-text-primary)">
                56%
              </span>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-(--color-primary)">
                <TrendingUp size={15} /> +14%
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-(--color-primary-50) text-(--color-primary) border border-(--color-primary-100)">
                <Zap size={13} /> 9-day streak
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/20">
                <Trophy size={13} /> 20 flags captured
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <div className="relative w-[104px] h-[104px]">
              <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                <circle cx="60" cy="60" r="52" fill="none" stroke="var(--color-border)" strokeWidth="10" />
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="var(--color-primary)"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 52}`}
                  strokeDashoffset={`${2 * Math.PI * 52 * (1 - 0.56)}`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="qv-num text-[26px] leading-none text-(--color-text-primary)">56%</span>
                <span className="text-[9px] uppercase tracking-[0.18em] text-(--color-text-muted) mt-1">Term</span>
              </div>
            </div>
            <div className="hidden sm:block text-xs text-(--color-text-muted) space-y-1.5">
              <div className="flex items-center gap-1.5"><CheckCircle size={13} className="text-(--color-primary)" /> 2 courses in progress</div>
              <div className="flex items-center gap-1.5"><Target size={13} className="text-(--color-text-secondary)" /> Next deadline in 3 days</div>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <section>
          <h2 className="text-xs font-semibold mb-3 text-(--color-text-primary)">
            Your Progress
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {/* Courses Enrolled */}
            <div className="qv-card qv-noise p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-(--color-primary)/20 flex items-center justify-center">
                  <BookOpen size={18} className="text-(--color-primary)" />
                </div>
                <span className="text-sm text-(--color-text-primary)">
                  Courses Enrolled
                </span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="qv-num text-[32px] leading-none text-(--color-text-primary)">
                  5
                </span>
              </div>
              <div className="text-(--color-text-muted) text-sm">
                3 active, 2 completed
              </div>
            </div>

            {/* Flags Captured */}
            <div className="qv-card qv-noise p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Flag size={18} className="text-green-400" />
                </div>
                <span className="text-sm text-(--color-text-primary)">
                  Flags Captured
                </span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="qv-num text-[32px] leading-none text-(--color-text-primary)">
                  38
                </span>
              </div>
              <div className="flex items-center gap-1 text-(--color-primary) text-sm">
                <TrendingUp size={14} />
                <span>+8 this week</span>
              </div>
            </div>

            {/* Learning Hours */}
            <div className="qv-card qv-noise p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Clock size={18} className="text-blue-400" />
                </div>
                <span className="text-sm text-(--color-text-primary)">
                  Learning Hours
                </span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="qv-num text-[32px] leading-none text-(--color-text-primary)">
                  32.5h
                </span>
              </div>
              <div className="text-sm text-(--color-text-muted)">
                This month
              </div>
            </div>

            {/* Achievements */}
            <div className="qv-card qv-noise p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Trophy size={18} className="text-yellow-400" />
                </div>
                <span className="text-sm text-(--color-text-primary)">
                  Achievements
                </span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="qv-num text-[32px] leading-none text-(--color-text-primary)">
                  12
                </span>
              </div>
              <div className="flex items-center gap-1 text-(--color-primary) text-sm">
                <TrendingUp size={14} />
                <span>+3 earned recently</span>
              </div>
            </div>
          </div>
        </section>

        {/* Active Courses */}
        <section>
          <h2 className="text-xs font-semibold mb-3 text-(--color-text-primary)">
            Active Courses
          </h2>
          <div className="qv-card qv-noise">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-(--color-text-primary) border-b border-(--color-border)">
                    <th className="px-5 py-3 text-left font-bold">
                      Course Name
                    </th>
                    <th className="px-5 py-3 text-left font-bold">Progress</th>
                    <th className="px-5 py-3 text-left font-bold">
                      Flags Captured
                    </th>
                    <th className="px-5 py-3 text-left font-bold">
                      Next Deadline
                    </th>
                    <th className="px-5 py-3 text-left font-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {activeCourses.map((course, idx) => (
                    <tr
                      key={idx}
                      className="border-t border-(--color-border) hover:bg-(--color-bg-overlay) dark:hover:bg-white/5 transition-colors"
                    >
                      <td className="px-5 py-3">
                        <div className="font-medium text-(--color-text-primary)">
                          {course.title}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-(--color-border) rounded-full h-2">
                            <div
                              className="bg-(--color-primary) h-2 rounded-full transition-all duration-300"
                              style={{ width: `${course.progress}%` }}
                            />
                          </div>
                          <span className="text-xs text-(--color-text-muted)">
                            {course.progress}%
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-(--color-text-muted)">
                        {course.flagsCaptured}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5 text-(--color-text-muted)">
                          <Calendar size={14} />
                          <span>{course.nextDeadline}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            course.status === "Near Completion"
                              ? "bg-yellow-500/15 text-yellow-400 border border-yellow-400/30"
                              : "bg-green-500/15 text-green-400 border border-green-400/30"
                          }`}
                        >
                          {course.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Recent Achievements */}
        <section>
          <h2 className="text-xs font-semibold mb-3 text-(--color-text-primary)">
            Recent Achievements
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recentAchievements.map((achievement, idx) => (
              <div
                key={idx}
                className="qv-card qv-noise p-5"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center ${achievement.color}`}
                  >
                    <achievement.icon size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-(--color-text-primary) mb-1">
                      {achievement.title}
                    </h3>
                    <p className="text-xs text-(--color-text-muted) mb-2">
                      {achievement.description}
                    </p>
                    <span className="text-xs text-(--color-text-muted)">
                      {achievement.date}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </UniversityStudentLayout>
  );
}
